

export type TypographyConfig = {
  [key: string]: {
    fontSize: string;
    fontWeight?: string;
    fontStyle?: string;
  };
};

export const formatFontForGoogle = (font: string): string => {
  const cleanFont = font.split(",")[0].trim(); // Remove fallback font
  return cleanFont.replace(/\s+/g, "+"); // Replace spaces with '+'
};

export function generateTypographyCSS(typography: TypographyConfig): string {
  if (!typography) return "";

  // Convert px string (e.g. "24px") to rem string (e.g. "1.5rem")
  const convertToRem = (px: string): string => {
    const baseSize = 16;
    const value = parseFloat(px);
    if (isNaN(value)) {
      console.warn(`Invalid fontSize: ${px}, defaulting to 1rem`);
      return "1rem";
    }
    return `${value / baseSize}rem`;
  };

  // Sanitize class or element name: lowercase, replace spaces with '-', remove invalid chars
  const sanitizeSelector = (name: string): string =>
    name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

  // Generate a CSS rule string for a given selector and config
  const generateClass = (
    selector: string,
    config: { fontSize: string; fontWeight?: string; fontStyle?: string }
  ): string => {
    const { fontSize, fontWeight, fontStyle } = config;
    const remFontSize = convertToRem(fontSize);
    const minSize = parseFloat(remFontSize) * 0.6;
    const maxSize = parseFloat(remFontSize) * 1;
    const clampFontSize = `clamp(${minSize}rem, ${parseFloat(remFontSize) * 1.3}vw + ${minSize}rem, ${maxSize}rem)`;

    let fontWeightCSS = fontWeight ? `font-weight: ${fontWeight};` : "";
    let fontStyleCSS = fontStyle ? `font-style: ${fontStyle};` : "";

    // Special handling if fontWeight includes italic or oblique, like "700italic"
    const weightMatch = fontWeight?.match(/^(\d+)(italic|oblique)?$/);
    if (weightMatch) {
      fontWeightCSS = `font-weight: ${weightMatch[1]};`;
      fontStyleCSS = weightMatch[2] ? `font-style: ${weightMatch[2]};` : "";
    }

    return `${selector} { font-size: ${clampFontSize}; ${fontWeightCSS} ${fontStyleCSS} }`;
  };

  const cssRules: string[] = [];

  // For each typography key, generate both .class and element selector rules
  for (const key in typography) {
    const sanitized = sanitizeSelector(key);
    cssRules.push(generateClass(`.${sanitized}`, typography[key]));
    cssRules.push(generateClass(sanitized, typography[key]));
  }

  // For the "Body Font", generate a `body` selector rule so <p> etc inherit it
  if ("Body Font" in typography) {
    cssRules.push(generateClass("body", typography["Body Font"]));
  } else {
    console.warn("Body Font not found in typography config");
  }

  return cssRules.join("\n");
}

// utils/getMainStyle.ts
export function getMainStyle(template: string): React.CSSProperties {
  const paddingTop = template === "design2" || template === "design3" ? "60px" : "0px";
  return { paddingTop };
}


export async function retry<T>(
  fn: () => Promise<T>,
  retries = 3,
  delay = 1000
): Promise<T> {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (err) {
      if (i === retries - 1) throw err;
      await new Promise((res) => setTimeout(res, delay));
    }
  }
  throw new Error("Unreachable"); // to satisfy TS that function always returns or throws
}

export function capitalizeWords(str: string): string {
  return str
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}


export function removeNullWords(input: string | null | undefined): string {
  if (!input) return "";

  return input
    .split(" ")
    .filter(word => word.toLowerCase() !== "null")
    .join(" ")
    .trim();
}


export function mapRoleName(input: string): string {
  // Normalize the input
  const normalized = input
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const roleMap: Record<string, string> = {
    "branch manager": "manager",
    "account manager": "account officer",
    "account officer": "account officer",
    "manager": "manager",
    "staff": "staff",
  };

  const mapped = roleMap[normalized] || normalized;

  // Capitalize each word before returning
  return capitalizeWords(mapped);
}



export function stripQueryParams(url: string): string {
  const queryIndex = url.indexOf("?");
  return queryIndex !== -1 ? url.slice(0, queryIndex) : url;
}













